/**
 * Ported from yaqatw's src/service/cloud/googledrive.ts. Only the import
 * paths (./events, ./log) and the client-id env var
 * (GOOGLE_DRIVE_CLIENT_ID → REACT_APP_GOOGLE_DRIVE_CLIENT_ID, CRA's
 * required prefix for inlining) differ from the source.
 */
import Events from "./events";
import { BaseCloudProvider } from "./BaseCloudProvider";
import type { FileMetadata, PushResult, UploadResult } from "./types";
import { log } from "./log";
import { maxRetryAttempts } from "./retryPolicy";

class GoogleDriveClass extends BaseCloudProvider {
  public readonly tokenName = "googleAccessToken";
  protected readonly providerName = "Google Drive";

  private clientId = process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID;
  private isDevelopment = process.env.NODE_ENV === "development";
  // `openid email` yields a verified email for collaboration identity (see
  // getIdentity); `drive.file` is the sync scope. NOTE: adding the identity
  // scopes changes the OAuth consent screen — existing users must re-consent
  // (re-authenticate) once for getIdentity to return an email.
  private scope = "openid email https://www.googleapis.com/auth/drive.file";
  private appFolderId: string | null = null;
  private appFolderPromise: Promise<string> | null = null;
  private folderCache: Map<string, string> = new Map();
  private folderResolutionPromises: Map<string, Promise<string>> = new Map();

  // GDrive: 10 req/sec safe rate
  protected readonly MIN_REQUEST_INTERVAL_MS = 100;

  protected async performAuthenticate(): Promise<string> {
    this.assertClientId(this.clientId, "REACT_APP_GOOGLE_DRIVE_CLIENT_ID");
    return super.performAuthenticate();
  }

  protected getAuthUrl(): string {
    const params = new URLSearchParams({
      client_id: this.clientId!,
      redirect_uri: this.redirectUri,
      response_type: "token",
      scope: this.scope,
      state: Math.random().toString(36).substring(7),
    });
    return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  }

  /**
   * GDrive override: also retries once on network errors (ERR_FAILED).
   */
  protected async fetchWithAuth(
    url: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<Response> {
    await this.throttle();

    let token = await this.getAccessToken();
    options.headers = { ...options.headers, Authorization: `Bearer ${token}` };

    let response: Response | undefined;
    let networkRetry = 0;
    const maxNetworkRetries = 1;

    while (networkRetry < maxNetworkRetries + 1) {
      try {
        response = await fetch(url, {
          ...options,
          signal: AbortSignal.timeout(this.FETCH_TIMEOUT_MS),
        });
        break;
      } catch (fetchError) {
        if (networkRetry < maxNetworkRetries) {
          console.warn("Network error, attempting to refresh token and retry...", fetchError);
          token = await this.refreshAccessToken();
          options.headers = { ...options.headers, Authorization: `Bearer ${token}` };
          networkRetry++;
        } else {
          throw fetchError;
        }
      }
    }

    if (response!.status === 401) {
      console.warn(`[${this.providerName}] Received 401, refreshing token...`);
      token = await this.refreshAccessToken();
      options.headers = { ...options.headers, Authorization: `Bearer ${token}` };
      response = await fetch(url, {
        ...options,
        signal: AbortSignal.timeout(this.FETCH_TIMEOUT_MS),
      });
    }

    if (response!.status === 429) {
      if (retryCount > maxRetryAttempts()) {
        throw new Error(`${this.providerName}: rate limit exceeded and max retries reached.`);
      }
      const retryAfter = response!.headers.get("Retry-After");
      const waitTime = retryAfter
        ? parseInt(retryAfter, 10) * 1000
        : 2000 * Math.pow(2, retryCount);
      console.warn(`[${this.providerName}] Rate limited (429). Retrying in ${waitTime}ms...`);
      await new Promise((r) => setTimeout(r, waitTime));
      return this.fetchWithAuth(url, options, retryCount + 1);
    }

    return response!;
  }

  /**
   * Non-interactive collaboration identity via the `openid email` scope:
   * `googledrive:<email>` (falling back to the stable `sub`). Uses the stored
   * token with a plain fetch (no re-auth / popup); returns null if not signed in,
   * the token is stale, or the token predates the identity scopes (the user
   * must re-authenticate once after the scope change).
   */
  public async getIdentity(): Promise<string | null> {
    const token = localStorage.getItem(this.tokenName);
    if (!token) return null;
    try {
      const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
        headers: { Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(this.FETCH_TIMEOUT_MS),
      });
      if (!res.ok) return null;
      const data = await res.json();
      const id = data?.email || data?.sub;
      return id ? `googledrive:${id}` : null;
    } catch {
      return null;
    }
  }

  private async ensureAppFolder(): Promise<string> {
    if (this.appFolderId) return this.appFolderId;
    if (this.appFolderPromise) return this.appFolderPromise;

    this.appFolderPromise = (async () => {
      let folderName = "YaqatW-DATA";
      if (this.isDevelopment) folderName += "-DEV";

      const appProperties = {
        yaqatw: this.isDevelopment ? "dev" : "true",
        version: "1.0",
      };

      try {
        const q2 = [
          "mimeType='application/vnd.google-apps.folder'",
          "trashed=false",
          `appProperties has { key='yaqatw' and value='${this.isDevelopment ? "dev" : "true"}' }`,
        ].join(" and ");

        const searchResponse = await this.fetchWithAuth(
          `https://www.googleapis.com/drive/v3/files?` +
            `q=${encodeURIComponent(q2)}` +
            `&spaces=drive` +
            `&pageSize=1` +
            `&fields=files(id,name)`
        );

        if (!searchResponse.ok) {
          throw new Error(`Search failed with status ${searchResponse.status}`);
        }

        const searchData = await searchResponse.json();
        if (searchData.files && searchData.files.length > 0) {
          this.appFolderId = searchData.files[0].id;
          return this.appFolderId!;
        }

        const createResponse = await this.fetchWithAuth(
          "https://www.googleapis.com/drive/v3/files?supportsAllDrives=true",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: folderName,
              mimeType: "application/vnd.google-apps.folder",
              parents: ["root"],
              appProperties,
            }),
          }
        );

        if (!createResponse.ok) {
          throw new Error(`Create failed with status ${createResponse.status}`);
        }

        const createData = await createResponse.json();
        this.appFolderId = createData.id;
        return this.appFolderId!;
      } catch (error) {
        Events.emit("showStatus", {
          intent: "error",
          text: "No internet connexion. Content not synced to Google Drive!",
        });
        console.error("Error ensuring app folder:", error);
        this.appFolderPromise = null;
        throw error;
      }
    })();

    return this.appFolderPromise;
  }

  private async resolveOrCreateFolder(
    parentFolderId: string,
    segment: string,
    currentPath: string
  ): Promise<string> {
    if (this.folderCache.has(currentPath)) return this.folderCache.get(currentPath)!;
    if (this.folderResolutionPromises.has(currentPath)) {
      return this.folderResolutionPromises.get(currentPath)!;
    }
    const resolutionPromise = (async () => {
      try {
        const searchResponse = await this.fetchWithAuth(
          `https://www.googleapis.com/drive/v3/files?q=name='${segment}' and mimeType='application/vnd.google-apps.folder' and '${parentFolderId}' in parents and trashed=false&spaces=drive&pageSize=1`
        );
        if (!searchResponse.ok) {
          throw new Error(`Search failed with status ${searchResponse.status}`);
        }
        const searchData = await searchResponse.json();
        if (searchData.files && searchData.files.length > 0) {
          const foundId = searchData.files[0].id;
          this.folderCache.set(currentPath, foundId);
          return foundId;
        }
        const createResponse = await this.fetchWithAuth(
          "https://www.googleapis.com/drive/v3/files?supportsAllDrives=true",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: segment,
              mimeType: "application/vnd.google-apps.folder",
              parents: [parentFolderId],
            }),
          }
        );
        if (!createResponse.ok) {
          throw new Error(`Create failed with status ${createResponse.status}`);
        }
        const createData = await createResponse.json();
        const newId = createData.id;
        this.folderCache.set(currentPath, newId);
        return newId;
      } catch (error) {
        console.error(`Error resolving path part ${segment}:`, error);
        this.folderResolutionPromises.delete(currentPath);
        throw error;
      }
    })();
    this.folderResolutionPromises.set(currentPath, resolutionPromise);
    return resolutionPromise;
  }

  async resolveFolderPath(fullPath: string): Promise<string> {
    let currentFolderId = await this.ensureAppFolder();
    if (typeof fullPath !== "string") {
      console.error("resolveFolderPath: fullPath is not a string", fullPath);
      throw new TypeError("resolveFolderPath: fullPath must be a string, got: " + typeof fullPath);
    }
    const pathParts = fullPath
      .toLowerCase()
      .split("/")
      .filter((p) => p);
    let currentPath = "";
    for (const part of pathParts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      currentFolderId = await this.resolveOrCreateFolder(currentFolderId, part, currentPath);
    }
    return currentFolderId;
  }

  async resolveFilePath(fullPath: string): Promise<{ parentFolderId: string; fileName: string | null }> {
    let currentFolderId = await this.ensureAppFolder();
    if (typeof fullPath !== "string") {
      console.error("resolveFilePath: fullPath is not a string", fullPath);
      throw new TypeError("resolveFilePath: fullPath must be a string, got: " + typeof fullPath);
    }
    const pathParts = fullPath
      .toLowerCase()
      .split("/")
      .filter((p) => p);
    const fileName = pathParts.length > 0 ? pathParts.pop()! : null;
    let currentPath = "";
    for (const part of pathParts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      currentFolderId = await this.resolveOrCreateFolder(currentFolderId, part, currentPath);
    }
    return { parentFolderId: currentFolderId, fileName };
  }

  /**
   * Resolves a file path to a parent folder ID and filename.
   */
  private async resolvePath(
    fullPath: string,
    isFolder: boolean = false
  ): Promise<{ parentFolderId: string; fileName: string | null }> {
    if (isFolder) {
      const folderId = await this.resolveFolderPath(fullPath);
      return { parentFolderId: folderId, fileName: null };
    }
    return this.resolveFilePath(fullPath);
  }

  public async pushDiff(globalEntries: any[]): Promise<void> {
    for (const entry of globalEntries) {
      await this.push(entry.path, entry.data);
      Events.emit("showStatus", { intent: "info", text: `Synced ${entry.path} to Google Drive.` });
    }
  }

  public async getMetadataAll(baseFolder: string): Promise<FileMetadata[]> {
    const allFiles: FileMetadata[] = [];
    try {
      const traverse = async (folderPath: string) => {
        const { parentFolderId } = await this.resolvePath(folderPath, true);
        const listResponse = await this.fetchWithAuth(
          `https://www.googleapis.com/drive/v3/files?q='${parentFolderId}' in parents and trashed=false&spaces=drive&pageSize=1000&fields=files(id,name,mimeType,parents,createdTime,modifiedTime)`
        );
        if (!listResponse.ok) {
          throw new Error(`List files failed with status ${listResponse.status}`);
        }
        const listData = await listResponse.json();
        for (const file of listData.files) {
          if (file.mimeType === "application/vnd.google-apps.folder") {
            await traverse(folderPath + "/" + file.name);
          } else {
            const pathParts = folderPath.split("/").filter((p: string) => p);
            const path = pathParts.join("/") + "/" + file.name;
            allFiles.push({
              id: file.id,
              name: file.name,
              path: path,
              modifiedTime: file.modifiedTime,
            });
          }
        }
      };
      await traverse(baseFolder);
    } catch (error) {
      console.error(`Error getting metadata for all files under ${baseFolder}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
    return allFiles;
  }

  public async getMetadata(path: string): Promise<FileMetadata[]> {
    try {
      const { parentFolderId } = await this.resolvePath(path.toLowerCase(), false);
      const listResponse = await this.fetchWithAuth(
        `https://www.googleapis.com/drive/v3/files?q='${parentFolderId}' in parents and mimeType!='application/vnd.google-apps.folder' and trashed=false&spaces=drive&pageSize=1000&fields=files(id,name,modifiedTime)`
      );
      if (!listResponse.ok) {
        throw new Error(`List files failed with status ${listResponse.status}`);
      }
      const listData = await listResponse.json();
      return (listData.files || []).map((f: any) => ({
        id: f.id,
        name: f.name,
        path: f.name,
        modifiedTime: f.modifiedTime,
      }));
    } catch (error) {
      console.error(`Error getting metadata for ${path}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async pullById(fileId: string): Promise<any> {
    try {
      const downloadResponse = await this.fetchWithAuth(
        `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`
      );
      if (!downloadResponse.ok) return null;
      const fileContent = await downloadResponse.text();
      return JSON.parse(fileContent);
    } catch (error) {
      console.error(`Error pulling by id ${fileId}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async pull(itemPath: string, fileId_: string | null = null): Promise<any> {
    try {
      let fileId = null;
      if (!fileId_) {
        const { parentFolderId, fileName: baseName } = await this.resolvePath(
          itemPath.toLowerCase(),
          false
        );
        if (!baseName || baseName === "null") return null;

        const response = await this.fetchWithAuth(
          `https://www.googleapis.com/drive/v3/files?q=name='${baseName}' and '${parentFolderId}' in parents and trashed=false&spaces=drive&pageSize=1`
        );
        if (!response.ok) {
          throw new Error(`Search failed with status ${response.status}`);
        }
        const data = await response.json();
        fileId = data.files?.[0]?.id;
      } else {
        fileId = fileId_;
      }

      if (!fileId) return null;

      const downloadResponse = await this.fetchWithAuth(
        `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`
      );
      if (!downloadResponse.ok) {
        throw new Error(`Download failed with status ${downloadResponse.status}`);
      }
      const fileContent = await downloadResponse.text();
      return JSON.parse(fileContent);
    } catch (error) {
      console.error(`Error pulling ${itemPath}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }

  private async uploadToGoogleDrive(
    fileName: string,
    data: any,
    isRetry: boolean = false
  ): Promise<PushResult | void> {
    try {
      const { parentFolderId, fileName: baseName } = await this.resolvePath(
        fileName.toLowerCase(),
        false
      );
      if (!fileName) return;

      const fileContent = JSON.stringify(data);
      const blob = new Blob([fileContent], { type: "application/json" });

      const searchResponse = await this.fetchWithAuth(
        `https://www.googleapis.com/drive/v3/files?q=name='${baseName}' and '${parentFolderId}' in parents and trashed=false&spaces=drive&pageSize=1`
      );

      if (!searchResponse.ok) {
        if (searchResponse.status === 404 && !isRetry) {
          console.warn(
            `[GoogleDrive] Search 404 for ${fileName}, clearing folder cache and retrying...`
          );
          this.folderCache.clear();
          this.folderResolutionPromises.clear();
          this.appFolderId = null;
          this.appFolderPromise = null;
          return this.uploadToGoogleDrive(fileName, data, true);
        }
        throw new Error(`Search failed with status ${searchResponse.status}`);
      }

      const searchData = await searchResponse.json();
      const existingFileId = searchData.files?.[0]?.id;

      let uploadResponse;
      if (existingFileId) {
        uploadResponse = await this.fetchWithAuth(
          `https://www.googleapis.com/upload/drive/v3/files/${existingFileId}?uploadType=media&fields=modifiedTime`,
          { method: "PATCH", body: blob }
        );
      } else {
        uploadResponse = await this.fetchWithAuth(
          "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=modifiedTime",
          {
            method: "POST",
            body: this.createMultipartBody(baseName, parentFolderId, blob),
          }
        );
      }

      if (!uploadResponse.ok) {
        if (uploadResponse.status === 404 && !isRetry) {
          console.warn(
            `[GoogleDrive] Upload 404 for ${fileName}, clearing folder cache and retrying...`
          );
          this.folderCache.clear();
          this.folderResolutionPromises.clear();
          this.appFolderId = null;
          this.appFolderPromise = null;
          return this.uploadToGoogleDrive(fileName, data, true);
        }
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }

      const responseData = await uploadResponse.json();
      if (responseData?.modifiedTime) return { modifiedTime: responseData.modifiedTime };
    } catch (error: any) {
      console.error(`Error uploading ${fileName}:`, error);
      throw error;
    }
  }

  public async downloadFile(filePath: string): Promise<Blob> {
    const { parentFolderId, fileName: baseName } = await this.resolvePath(
      filePath.toLowerCase(),
      false
    );
    if (!baseName || baseName === "null") {
      throw new Error(`GoogleDrive downloadFile: file not found: ${filePath}`);
    }
    const searchResponse = await this.fetchWithAuth(
      `https://www.googleapis.com/drive/v3/files?q=name='${baseName}' and '${parentFolderId}' in parents and trashed=false&spaces=drive&pageSize=1`
    );
    if (!searchResponse.ok) {
      throw new Error(`GoogleDrive search failed for ${filePath}: ${searchResponse.status}`);
    }
    const searchData = await searchResponse.json();
    const fileId = searchData.files?.[0]?.id;
    if (!fileId) {
      throw new Error(`GoogleDrive downloadFile: file not found: ${filePath}`);
    }
    const downloadResponse = await this.fetchWithAuth(
      `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`
    );
    if (!downloadResponse.ok) {
      throw new Error(`GoogleDrive download failed for ${filePath}: ${downloadResponse.status}`);
    }
    return downloadResponse.blob();
  }

  private createMultipartBody(fileName: string | null, folderId: string, blob: Blob): FormData {
    const formData = new FormData();
    const metadata = { name: fileName, parents: [folderId] };
    formData.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
    formData.append("file", blob);
    return formData;
  }

  public async push(fileName: string, data: any): Promise<PushResult | void> {
    return this.uploadToGoogleDrive(fileName.toLocaleLowerCase(), data);
  }

  public async uploadFile(
    filePath: string,
    fileContent: Blob | string | ArrayBuffer
  ): Promise<UploadResult> {
    try {
      const { parentFolderId, fileName: baseName } = await this.resolvePath(
        filePath.toLowerCase(),
        false
      );
      if (!baseName) return { webViewLink: undefined };

      const fileName = baseName;
      let blob: Blob;
      let contentType = "application/octet-stream";

      if (fileContent instanceof Blob) {
        blob = fileContent;
        contentType = fileContent.type || "application/octet-stream";
      } else if (fileContent instanceof ArrayBuffer) {
        blob = new Blob([fileContent], { type: contentType });
      } else if (typeof fileContent === "string") {
        if (fileName.endsWith(".json")) contentType = "application/json";
        else if (fileName.endsWith(".csv")) contentType = "text/csv";
        else if (fileName.endsWith(".html") || fileName.endsWith(".htm")) contentType = "text/html";
        else if (fileName.endsWith(".xml")) contentType = "application/xml";
        else contentType = "text/plain";
        blob = new Blob([fileContent], { type: contentType });
      } else {
        blob = new Blob([String(fileContent)], { type: "text/plain" });
      }

      const searchResponse = await this.fetchWithAuth(
        `https://www.googleapis.com/drive/v3/files?q=name='${fileName}' and '${parentFolderId}' in parents and trashed=false&spaces=drive&pageSize=1`
      );
      if (!searchResponse.ok) {
        throw new Error(`Search failed with status ${searchResponse.status}`);
      }

      const searchData = await searchResponse.json();
      const existingFileId = searchData.files?.[0]?.id;

      let uploadResponse;
      if (existingFileId) {
        uploadResponse = await this.fetchWithAuth(
          `https://www.googleapis.com/upload/drive/v3/files/${existingFileId}?uploadType=media&fields=webViewLink`,
          { method: "PATCH", body: blob }
        );
      } else {
        uploadResponse = await this.fetchWithAuth(
          "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=webViewLink",
          { method: "POST", body: this.createMultipartBody(fileName, parentFolderId, blob) }
        );
      }

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }

      const responseData = await uploadResponse.json();
      return { webViewLink: responseData?.webViewLink || undefined };
    } catch (error: any) {
      console.error(`Error uploading file ${filePath}:`, error);
      Events.emit("showError", `File upload failed: ${error.message}`);
      throw error;
    }
  }

  public async delete(filePath: string): Promise<void> {
    try {
      const { parentFolderId, fileName: baseName } = await this.resolvePath(
        filePath.toLowerCase(),
        false
      );
      if (!baseName || baseName === "null") {
        console.warn(`Cannot delete: invalid file name for path ${filePath}`);
        return;
      }

      const searchResponse = await this.fetchWithAuth(
        `https://www.googleapis.com/drive/v3/files?q=name='${baseName}' and '${parentFolderId}' in parents and trashed=false&spaces=drive&pageSize=1`
      );
      if (!searchResponse.ok) {
        throw new Error(`Search failed with status ${searchResponse.status}`);
      }

      const data = await searchResponse.json();
      const fileId = data.files?.[0]?.id;
      if (!fileId) {
        console.warn(`File not found for deletion: ${filePath}`);
        return;
      }

      const deleteResponse = await this.fetchWithAuth(
        `https://www.googleapis.com/drive/v3/files/${fileId}`,
        { method: "DELETE" }
      );
      if (!deleteResponse.ok) {
        throw new Error(`Delete failed with status ${deleteResponse.status}`);
      }
      log(`Successfully deleted ${filePath} from Google Drive`);
    } catch (error) {
      console.error(`Error deleting ${filePath}:`, error);
      Events.emit("showError", `Delete failed: ${(error as any).message}`);
      throw error;
    }
  }
}

const GoogleDrive = new GoogleDriveClass();
export default GoogleDrive;
